export type ValidateError = {
  code: number;
  message: string;
};

export function validateBik(bik: string, error: ValidateError) {
  let result = false;

  if (!bik.length) {
    error.code = 1;
    error.message = 'БИК пуст';
  } else if (/[^0-9]/.test(bik)) {
    error.code = 2;
    error.message = 'БИК может состоять только из цифр';
  } else if (bik.length !== 9) {
    error.code = 3;
    error.message = 'БИК может состоять только из 9 цифр';
  } else {
    result = true;
  }
  return result;
}

export function validateInn(inn: string, error: ValidateError) {
  let result = false;

  if (!inn.length) {
    error.code = 1;
    error.message = 'ИНН пуст';
  } else if (/[^0-9]/.test(inn)) {
    error.code = 2;
    error.message = 'ИНН может состоять только из цифр';
  } else if ([10, 12].indexOf(inn.length) === -1) {
    error.code = 3;
    error.message = 'ИНН может состоять только из 10 или 12 цифр';
  } else {
    const checkDigit = function (inn, coefficients) {
      let n = 0;
      for (const i in coefficients) {
        n += coefficients[i] * inn[i];
      }
      return parseInt(String((n % 11) % 10));
    };
    switch (inn.length) {
      case 10:
        const n10 = checkDigit(inn, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
        if (n10 === parseInt(inn[9])) {
          result = true;
        }
        break;
      case 12:
        const n11 = checkDigit(inn, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        const n12 = checkDigit(inn, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
        if (n11 === parseInt(inn[10]) && n12 === parseInt(inn[11])) {
          result = true;
        }
        break;
    }
    if (!result) {
      error.code = 4;
      error.message = 'Неправильное контрольное число ИНН';
    }
  }
  return result;
}

export function validateKpp(kpp: string, error: ValidateError) {
  let result = false;
  if (!kpp.length) {
    error.code = 1;
    error.message = 'КПП пуст';
  } else if (kpp.length !== 9) {
    error.code = 2;
    error.message =
      'КПП может состоять только из 9 знаков (цифр или заглавных букв латинского алфавита от A до Z)';
  } else if (!/^[0-9]{4}[0-9A-Z]{2}[0-9]{3}$/.test(kpp)) {
    error.code = 3;
    error.message = 'Неправильный формат КПП';
  } else {
    result = true;
  }
  return result;
}
export function validateOkpo(value: string, error: ValidateError) {
  let result = false;
  if (!value) {
    error.code = 1;
    error.message = 'ОКПО пуст';
  }
  if (value.length !== 8 && value.length !== 10) {
    error.code = 2;
    error.message = 'ОКПО может состоять только из 8 или 10 цифр';
    return result;
  }
  const expectedValue = Number(value.slice(-1));
  const getWeight = (index: number): number => {
    if (index < 10) {
      return index + 1;
    } else {
      return (index % 10) + 1;
    }
  };
  const testingValue = value.slice(0, -1);
  let summ = 0;
  for (const i in testingValue.split('')) {
    summ += Number(testingValue[i]) * getWeight(Number(i));
  }
  let del11 = summ % 11;
  if (del11 === 10) {
    summ = 0;
    for (const i in testingValue.split('')) {
      summ += Number(testingValue[i]) * (getWeight(Number(i)) + 2);
    }
    del11 = summ % 11;
  }
  if (del11 === expectedValue) {
    result = true;
  } else {
    error.code = 1;
    error.message = 'Неправильное контрольное число ОКПО';
  }
  return result;
}

export function validateKs(ks: string, bik: string, error: ValidateError) {
  let result = false;
  if (validateBik(bik, error)) {
    if (!ks.length) {
      error.code = 1;
      error.message = 'К/С пуст';
    } else if (/[^0-9]/.test(ks)) {
      error.code = 2;
      error.message = 'К/С может состоять только из цифр';
    } else if (ks.length !== 20) {
      error.code = 3;
      error.message = 'К/С может состоять только из 20 цифр';
    } else {
      const bikKs = '0' + bik.toString().slice(4, 6) + ks;
      let checksum = 0;
      const coefficients = [
        7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1,
      ];
      for (const i in coefficients) {
        checksum += coefficients[i] * (Number(bikKs[i]) % 10);
      }
      if (checksum % 10 === 0) {
        result = true;
      } else {
        error.code = 4;
        error.message = 'Неправильное контрольное число';
      }
    }
  }
  return result;
}

export function validateOgrn(ogrn: string, error: ValidateError) {
  let result = false;

  if (!ogrn.length) {
    error.code = 1;
    error.message = 'ОГРН пуст';
  } else if (/[^0-9]/.test(ogrn)) {
    error.code = 2;
    error.message = 'ОГРН может состоять только из цифр';
  } else if (ogrn.length !== 13) {
    error.code = 3;
    error.message = 'ОГРН может состоять только из 13 цифр';
  } else {
    const n13 = parseInt(
      (parseInt(ogrn.slice(0, -1)) % 11).toString().slice(-1)
    );
    if (n13 === parseInt(ogrn[12])) {
      result = true;
    } else {
      error.code = 4;
      error.message = 'Неправильный ОГРН';
    }
  }
  return result;
}

export function validateOgrnip(ogrnip: string, error: ValidateError) {
  let result = false;
  if (!ogrnip.length) {
    error.code = 1;
    error.message = 'ОГРНИП пуст';
  } else if (/[^0-9]/.test(ogrnip)) {
    error.code = 2;
    error.message = 'ОГРНИП может состоять только из цифр';
  } else if (ogrnip.length !== 15) {
    error.code = 3;
    error.message = 'ОГРНИП может состоять только из 15 цифр';
  } else {
    const n15 = parseInt(
      (parseInt(ogrnip.slice(0, -1)) % 13).toString().slice(-1)
    );
    if (n15 === parseInt(ogrnip[14])) {
      result = true;
    } else {
      error.code = 4;
      error.message = 'Неправильное контрольное число';
    }
  }
  return result;
}

export function validateRs(rs: string, bik: string, error: ValidateError) {
  let result = false;
  if (validateBik(bik, error)) {
    if (!rs.length) {
      error.code = 1;
      error.message = 'Р/С пуст';
    } else if (/[^0-9]/.test(rs)) {
      error.code = 2;
      error.message = 'Р/С может состоять только из цифр';
    } else if (rs.length !== 20) {
      error.code = 3;
      error.message = 'Р/С может состоять только из 20 цифр';
    } else {
      const bikRs = bik.toString().slice(-3) + rs;
      let checksum = 0;
      const coefficients = [
        7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1,
      ];
      for (const i in coefficients) {
        checksum += coefficients[i] * (Number(bikRs[i]) % 10);
      }
      if (checksum % 10 === 0) {
        result = true;
      } else {
        error.code = 4;
        error.message = 'Некорректный Р/С';
      }
    }
  }
  return result;
}
